import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { 
  Box, Stack, Typography, Button, Divider, Chip, Paper, 
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem,
  Grid, Card, CardContent, Avatar, IconButton
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import IosShareIcon from '@mui/icons-material/IosShare';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import GridOnIcon from '@mui/icons-material/GridOn';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CommentIcon from '@mui/icons-material/Comment';
import EditIcon from '@mui/icons-material/Edit';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import Zoom from '@mui/material/Zoom';

import { getIncident, addResponse, updateIncident, INCIDENT_STATUS, INCIDENT_PRIORITY } from './incidentsService';
import { useI18n } from '../../i18n/i18n';
import SafeImage from '../../components/SafeImage';
import ImageAnalysisPanel from './ImageAnalysisPanel';

export default function IncidentViewPage({ user }) {
  const { t } = useI18n();
  const { id } = useParams();
  const navigate = useNavigate();

  const [incident, setIncident] = useState(null);
  const [loading, setLoading] = useState(true);
  const [responseDialogOpen, setResponseDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [responseData, setResponseData] = useState({
    message: '',
    status: INCIDENT_STATUS.UNDER_REVIEW,
    priority: INCIDENT_PRIORITY.MEDIUM
  });
  const [statusData, setStatusData] = useState({
    status: '',
    priority: ''
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [showImageAnalysis, setShowImageAnalysis] = useState(false);

  useEffect(() => {
    const loadIncident = async () => {
      try {
        setLoading(true);
        const incidentData = await getIncident(id);
        
        if (!incidentData) {
          alert(t('incidents.not_found') || 'Incident not found');
          navigate('/incidents/list');
          return;
        }
        
        setIncident(incidentData);
        setStatusData({
          status: incidentData.status || INCIDENT_STATUS.OPEN,
          priority: incidentData.priority || INCIDENT_PRIORITY.MEDIUM
        });
      } catch (error) {
        console.error('Error loading incident:', error);
        alert(t('incidents.load_error') || 'Error loading incident');
        navigate('/incidents/list');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadIncident();
    }
  }, [id, navigate, t]);

  const canRespond = user && (user.role === 'supervisor' || user.role === 'technician' || user.role === 'admin');
  const canEditStatus = user && (user.role === 'supervisor' || user.role === 'technician' || user.role === 'admin');

  const handleAddResponse = async () => {
    if (!responseData.message.trim()) {
      alert(t('incidents.response_message_required') || 'Response message is required');
      return;
    }

    try {
      const updatedIncident = await addResponse(id, responseData, user);
      if (updatedIncident) {
        setIncident(updatedIncident);
        setResponseDialogOpen(false);
        setResponseData({ 
          message: '', 
          status: INCIDENT_STATUS.UNDER_REVIEW, 
          priority: INCIDENT_PRIORITY.MEDIUM 
        });
        alert(t('incidents.response_added') || 'Response added successfully');
      }
    } catch (error) {
      console.error('Error adding response:', error);
      alert(t('incidents.response_error') || 'Error adding response');
    }
  };

  const handleUpdateStatus = async () => {
    try {
      const updatedIncident = await updateIncident(id, {
        status: statusData.status,
        priority: statusData.priority
      });
      if (updatedIncident) {
        setIncident(updatedIncident);
        setStatusDialogOpen(false);
        alert(t('incidents.status_updated') || 'Status updated successfully');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert(t('incidents.status_error') || 'Error updating status');
    }
  };

  const handleImageClick = (photo) => {
    setSelectedImage(photo);
    setImageDialogOpen(true);
  };

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6">{t('common.loading') || 'Loading...'}</Typography>
      </Box>
    );
  }

  if (!incident) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" color="error">
          {t('incidents.not_found') || 'Incident not found'}
        </Typography>
        <Button 
          component={RouterLink} 
          to="/incidents/list"
          sx={{ mt: 2 }}
        >
          {t('INCIDENTS_BACK_TO_LIST') || 'Back to List'}
        </Button>
      </Box>
    );
  }

  const fileBase = `Incident_${safeFileName(incident.clientName || 'Unknown')}_${(incident.date || '').slice(0,10)}`;

  const handleSaveJson = async () => {
    try {
      const blob = new Blob([JSON.stringify(incident, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fileBase}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error saving JSON:', error);
      alert('Error saving file');
    }
  };

  const handleExportExcel = async () => {
    try {
      const XLSX = (await import('xlsx')).default;
      const wb = XLSX.utils.book_new();

      const details = [
        ['Field', 'Value'],
        [t('common.date'), formatDate(incident.date)],
        [t('incidents.responsible'), incident.responsible || ''],
        [t('incidents.client'), incident.clientName || incident.clientId || ''],
        [t('incidents.type'), incident.type || ''],
        [t('common.status'), t(getStatusText(incident.status))],
        [t('incidents.priority'), t(getPriorityText(incident.priority))],
        [t('incidents.description'), incident.description || ''],
        [t('incidents.photos'), Array.isArray(incident.photos) ? incident.photos.length : 0],
        [t('incidents.responses'), Array.isArray(incident.responses) ? incident.responses.length : 0],
        ['Created', formatDateTime(incident.createdAt)],
      ];
      
      const ws1 = XLSX.utils.aoa_to_sheet(details);
      XLSX.utils.book_append_sheet(wb, ws1, 'Incident Details');

      if (Array.isArray(incident.responses) && incident.responses.length > 0) {
        const responsesData = incident.responses.map((response, index) => ({
          'No': index + 1,
          'Date': formatDateTime(response.createdAt),
          'User': response.createdBy?.name || 'Unknown',
          'Role': response.createdBy?.role || 'Unknown',
          'Status': t(getStatusText(response.status)),
          'Priority': t(getPriorityText(response.priority)),
          'Message': response.message,
        }));
        
        const ws2 = XLSX.utils.json_to_sheet(responsesData);
        XLSX.utils.book_append_sheet(wb, ws2, 'Responses');
      }

      if (Array.isArray(incident.photos) && incident.photos.length > 0) {
        const photosData = incident.photos.map((photo, index) => ({
          'No': index + 1,
          'Name': photo.name || `Photo ${index + 1}`,
          'Date': formatDateTime(photo.timestamp),
        }));
        
        const ws3 = XLSX.utils.json_to_sheet(photosData);
        XLSX.utils.book_append_sheet(wb, ws3, 'Photos');
      }

      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fileBase}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('Error exporting to Excel');
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Діалог для збільшення фото */}
      <Dialog 
        open={imageDialogOpen} 
        onClose={() => setImageDialogOpen(false)}
        maxWidth="lg"
        TransitionComponent={Zoom}
      >
        <DialogContent>
          {selectedImage && (
            <SafeImage 
              src={selectedImage.dataUrl} 
              alt={selectedImage.name}
              style={{ 
                maxWidth: '90vw', 
                maxHeight: '90vh',
                width: 'auto',
                height: 'auto'
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImageDialogOpen(false)}>
            {t('common.close') || 'Close'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Діалог для додавання відповіді */}
      <Dialog 
        open={responseDialogOpen} 
        onClose={() => setResponseDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {t('incidents.add_response') || 'Add Response'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>{t('common.status') || 'Status'}</InputLabel>
              <Select
                value={responseData.status}
                label={t('common.status') || 'Status'}
                onChange={(e) => setResponseData(prev => ({ ...prev, status: e.target.value }))}
              >
                <MenuItem value={INCIDENT_STATUS.UNDER_REVIEW}>
                  {t('incident_status_under_review') || 'Under Review'}
                </MenuItem>
                <MenuItem value={INCIDENT_STATUS.IN_PROGRESS}>
                  {t('incident_status_in_progress') || 'In Progress'}
                </MenuItem>
                <MenuItem value={INCIDENT_STATUS.WAITING_FOR_APPROVAL}>
                  {t('incident_status_waiting_for_approval') || 'Waiting for Approval'}
                </MenuItem>
                <MenuItem value={INCIDENT_STATUS.RESOLVED}>
                  {t('incident_status_resolved') || 'Resolved'}
                </MenuItem>
                <MenuItem value={INCIDENT_STATUS.CLOSED}>
                  {t('incident_status_closed') || 'Closed'}
                </MenuItem>
                <MenuItem value={INCIDENT_STATUS.REOPENED}>
                  {t('incident_status_reopened') || 'Reopened'}
                </MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>{t('incidents.priority') || 'Priority'}</InputLabel>
              <Select
                value={responseData.priority}
                label={t('incidents.priority') || 'Priority'}
                onChange={(e) => setResponseData(prev => ({ ...prev, priority: e.target.value }))}
              >
                <MenuItem value={INCIDENT_PRIORITY.LOW}>
                  {t('incident_priority_low') || 'Low'}
                </MenuItem>
                <MenuItem value={INCIDENT_PRIORITY.MEDIUM}>
                  {t('incident_priority_medium') || 'Medium'}
                </MenuItem>
                <MenuItem value={INCIDENT_PRIORITY.HIGH}>
                  {t('incident_priority_high') || 'High'}
                </MenuItem>
                <MenuItem value={INCIDENT_PRIORITY.CRITICAL}>
                  {t('incident_priority_critical') || 'Critical'}
                </MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              label={t('incidents.response_message') || 'Response Message'}
              multiline
              rows={4}
              value={responseData.message}
              onChange={(e) => setResponseData(prev => ({ ...prev, message: e.target.value }))}
              placeholder={t('incidents.response_placeholder') || 'Enter your response...'}
              fullWidth
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResponseDialogOpen(false)}>
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button 
            onClick={handleAddResponse} 
            variant="contained"
            disabled={!responseData.message.trim()}
          >
            {t('incidents.add_response') || 'Add Response'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Діалог для зміни статусу */}
      <Dialog 
        open={statusDialogOpen} 
        onClose={() => setStatusDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('incidents.update_status') || 'Update Status'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>{t('common.status') || 'Status'}</InputLabel>
              <Select
                value={statusData.status}
                label={t('common.status') || 'Status'}
                onChange={(e) => setStatusData(prev => ({ ...prev, status: e.target.value }))}
              >
                <MenuItem value={INCIDENT_STATUS.OPEN}>
                  {t('incident_status_open') || 'Open'}
                </MenuItem>
                <MenuItem value={INCIDENT_STATUS.UNDER_REVIEW}>
                  {t('incident_status_under_review') || 'Under Review'}
                </MenuItem>
                <MenuItem value={INCIDENT_STATUS.IN_PROGRESS}>
                  {t('incident_status_in_progress') || 'In Progress'}
                </MenuItem>
                <MenuItem value={INCIDENT_STATUS.WAITING_FOR_APPROVAL}>
                  {t('incident_status_waiting_for_approval') || 'Waiting for Approval'}
                </MenuItem>
                <MenuItem value={INCIDENT_STATUS.RESOLVED}>
                  {t('incident_status_resolved') || 'Resolved'}
                </MenuItem>
                <MenuItem value={INCIDENT_STATUS.CLOSED}>
                  {t('incident_status_closed') || 'Closed'}
                </MenuItem>
                <MenuItem value={INCIDENT_STATUS.REOPENED}>
                  {t('incident_status_reopened') || 'Reopened'}
                </MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>{t('incidents.priority') || 'Priority'}</InputLabel>
              <Select
                value={statusData.priority}
                label={t('incidents.priority') || 'Priority'}
                onChange={(e) => setStatusData(prev => ({ ...prev, priority: e.target.value }))}
              >
                <MenuItem value={INCIDENT_PRIORITY.LOW}>
                  {t('incident_priority_low') || 'Low'}
                </MenuItem>
                <MenuItem value={INCIDENT_PRIORITY.MEDIUM}>
                  {t('incident_priority_medium') || 'Medium'}
                </MenuItem>
                <MenuItem value={INCIDENT_PRIORITY.HIGH}>
                  {t('incident_priority_high') || 'High'}
                </MenuItem>
                <MenuItem value={INCIDENT_PRIORITY.CRITICAL}>
                  {t('incident_priority_critical') || 'Critical'}
                </MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)}>
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button 
            onClick={handleUpdateStatus} 
            variant="contained"
          >
            {t('incidents.update_status') || 'Update Status'}
          </Button>
        </DialogActions>
      </Dialog>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate('/incidents/list')} 
          variant="text"
        >
          {t('COMMON_BACK') || 'Back'}
        </Button>
      </Stack>

      <Paper sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
          <div>
            <Typography variant="h4" sx={{ mb: 1 }}>
              {t('incidents.view_title') || 'Incident Details'}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {formatDateTime(incident.date)} • {incident.type}
            </Typography>
          </div>

          <Stack direction="row" spacing={1}>
            {(canRespond || canEditStatus) && (
              <>
                {canRespond && (
                  <Button 
                    startIcon={<CommentIcon />} 
                    variant="contained"
                    onClick={() => setResponseDialogOpen(true)}
                  >
                    {t('incidents.add_response') || 'Add Response'}
                  </Button>
                )}
                {canEditStatus && (
                  <IconButton 
                    color="primary"
                    onClick={() => setStatusDialogOpen(true)}
                    title={t('incidents.update_status') || 'Update Status'}
                  >
                    <EditIcon />
                  </IconButton>
                )}
              </>
            )}
            {Array.isArray(incident.photos) && incident.photos.length > 0 && (
              <Button 
                startIcon={<AnalyticsIcon />} 
                variant="outlined"
                onClick={() => setShowImageAnalysis(!showImageAnalysis)}
              >
                {t('image_analysis_auto_detection') || 'AI Аналіз фото'}
              </Button>
            )}
            <Button startIcon={<SaveIcon />} variant="outlined" onClick={handleSaveJson}>
              {t('ACTIONS_SAVE_JSON') || 'Save as JSON'}
            </Button>
            <Button startIcon={<GridOnIcon />} variant="outlined" onClick={handleExportExcel}>
              {t('ACTIONS_EXPORT_EXCEL') || 'Export Excel'}
            </Button>
          </Stack>
        </Stack>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Stack spacing={2}>
              <DetailRow 
                label={t('incidents.responsible')} 
                value={incident.responsible} 
              />
              <DetailRow 
                label={t('incidents.client')} 
                value={incident.clientName || incident.clientId} 
              />
              <DetailRow 
                label={t('incidents.type')} 
                value={
                  <Chip 
                    label={incident.type} 
                    size="small" 
                    color={getTypeChipColor(incident.type)}
                  />
                } 
              />
              <DetailRow 
                label={t('common.status')} 
                value={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip 
                      label={t(getStatusText(incident.status))} 
                      size="small"
                      color={getStatusChipColor(incident.status)}
                    />
                    {canEditStatus && (
                      <IconButton 
                        size="small"
                        onClick={() => setStatusDialogOpen(true)}
                        title={t('incidents.update_status') || 'Update Status'}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                } 
              />
              <DetailRow 
                label={t('incidents.priority')} 
                value={
                  <Chip 
                    label={t(getPriorityText(incident.priority))} 
                    size="small"
                    color={getPriorityChipColor(incident.priority)}
                  />
                } 
              />
            </Stack>
          </Grid>

          <Grid item xs={12} md={6}>
            <DetailRow 
              label={t('incidents.description')} 
              value={
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {incident.description}
                </Typography>
              } 
              fullWidth
            />
          </Grid>

          {/* Фото */}
          {Array.isArray(incident.photos) && incident.photos.length > 0 && (
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ mb: 2 }}>
                {t('incidents.photos')} ({incident.photos.length})
              </Typography>
              <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 2 }}>
                {incident.photos.map((photo, index) => (
                  <Box 
                    key={photo.id || index} 
                    sx={{ 
                      textAlign: 'center',
                      cursor: 'pointer',
                      '&:hover': { opacity: 0.8 }
                    }}
                    onClick={() => handleImageClick(photo)}
                  >
                    <SafeImage 
                      src={photo.dataUrl} 
                      alt={photo.name}
                      style={{ 
                        width: 150, 
                        height: 150, 
                        borderRadius: 1,
                        objectFit: 'cover'
                      }}
                    />
                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                      {photo.name || `Photo ${index + 1}`}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Grid>
          )}

          {/* AI Аналіз фото */}
          {showImageAnalysis && (
            <Grid item xs={12}>
              <ImageAnalysisPanel 
                incident={incident}
                onAnalysisComplete={() => {
                  console.log('Аналіз завершено');
                  // Можна оновити дані інциденту
                }}
              />
            </Grid>
          )}

          {/* Відповіді */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t('incidents.responses') || 'Responses'} ({Array.isArray(incident.responses) ? incident.responses.length : 0})
            </Typography>

            {Array.isArray(incident.responses) && incident.responses.length > 0 ? (
              <Stack spacing={2}>
                {incident.responses.map((response, index) => (
                  <Card key={response.id} variant="outlined">
                    <CardContent>
                      <Stack direction="row" spacing={2} alignItems="flex-start">
                        <Avatar sx={{ bgcolor: getRoleColor(response.createdBy?.role) }}>
                          {response.createdBy?.name?.charAt(0) || 'U'}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                            <Box>
                              <Typography variant="subtitle1" fontWeight="bold">
                                {response.createdBy?.name || 'Unknown User'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {response.createdBy?.role} • {formatDateTime(response.createdAt)}
                              </Typography>
                            </Box>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip 
                                label={t(getStatusText(response.status))} 
                                size="small"
                                color={getStatusChipColor(response.status)}
                              />
                              <Chip 
                                label={t(getPriorityText(response.priority))} 
                                size="small"
                                color={getPriorityChipColor(response.priority)}
                              />
                            </Stack>
                          </Stack>
                          <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                            {response.message}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            ) : (
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                {t('incidents.no_responses') || 'No responses yet'}
              </Typography>
            )}
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />
        
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button 
            component={RouterLink}
            to="/incidents/list"
            variant="outlined"
          >
            {t('INCIDENTS_BACK_TO_LIST') || 'Back to List'}
          </Button>
          {canRespond && (
            <Button 
              onClick={() => setResponseDialogOpen(true)}
              variant="contained"
              startIcon={<CommentIcon />}
            >
              {t('incidents.add_response') || 'Add Response'}
            </Button>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}

// Допоміжні компоненти та функції
function DetailRow({ label, value, fullWidth = false }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: fullWidth ? 'column' : 'row', gap: 1, alignItems: fullWidth ? 'flex-start' : 'center' }}>
      <Typography sx={{ minWidth: 120, fontWeight: 600, flexShrink: 0 }}>
        {label}:
      </Typography>
      {React.isValidElement(value) ? value : <Typography>{value || '-'}</Typography>}
    </Box>
  );
}

function formatDate(dateString) {
  try {
    return dateString ? new Date(dateString).toLocaleDateString() : '-';
  } catch {
    return dateString || '-';
  }
}

function formatDateTime(dateString) {
  try {
    return dateString ? new Date(dateString).toLocaleString() : '-';
  } catch {
    return dateString || '-';
  }
}

function safeFileName(str) {
  return (str || '').replace(/[^a-zA-Z0-9]/g, '_');
}

function getTypeChipColor(type) {
  const colors = {
    client: 'primary',
    technical: 'warning',
    logistics: 'success', 
    safety: 'error'
  };
  return colors[type] || 'default';
}

function getStatusChipColor(status) {
  const colors = {
    open: 'warning',
    under_review: 'info',
    in_progress: 'primary',
    waiting_for_approval: 'secondary',
    resolved: 'success',
    closed: 'default',
    reopened: 'error'
  };
  return colors[status] || 'default';
}

function getPriorityChipColor(priority) {
  const colors = {
    low: 'default',
    medium: 'primary',
    high: 'warning',
    critical: 'error'
  };
  return colors[priority] || 'default';
}

function getStatusText(status) {
  const statusMap = {
    open: 'incident_status_open',
    under_review: 'incident_status_under_review',
    in_progress: 'incident_status_in_progress',
    waiting_for_approval: 'incident_status_waiting_for_approval',
    resolved: 'incident_status_resolved',
    closed: 'incident_status_closed',
    reopened: 'incident_status_reopened'
  };
  return statusMap[status] || status;
}

function getPriorityText(priority) {
  const priorityMap = {
    low: 'incident_priority_low',
    medium: 'incident_priority_medium',
    high: 'incident_priority_high',
    critical: 'incident_priority_critical'
  };
  return priorityMap[priority] || priority;
}

function getRoleColor(role) {
  const colors = {
    admin: '#f44336',
    supervisor: '#2196f3',
    technician: '#ff9800',
    operator: '#4caf50'
  };
  return colors[role] || '#757575';
}